import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormControlLabel,
    Switch,
    Autocomplete,
    FormHelperText,
    Checkbox,
} from '@mui/material';
import {
    CheckBox as CheckBoxIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';
import { MobileDateTimePicker, MobileDatePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/cs';
import type { Akce, CreateAkceRequest } from '../../api/JsdhNotifikaceService/generated/models/Akce';
import type { Uzivatel } from '../../api/JsdhNotifikaceService/generated/models/Uzivatel';
import {
    useCreateAkceMutation,
    useUpdateAkceMutation,
} from '../../api/JsdhNotifikaceService/service/AkceMutations';
import { useAllUsersQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';
import { useTypyKontrolSplnitelneUcastiQuery } from '../../api/JsdhNotifikaceService/service/KontrolyMutations';

dayjs.locale('cs');

const TYP_LABELS: Record<string, string> = {
    SKOLENI: 'Školení',
    KONTROLA_TECHNIKY: 'Kontrola techniky',
    SCHUZKA: 'Schůzka',
    JINE: 'Jiné',
};

const OPAKOVANI_LABELS: Record<string, string> = {
    ZADNE: 'Žádné',
    TYDNE: 'Týdně',
    MESICNE: 'Měsíčně',
    CTVRTLETNE: 'Čtvrtletně',
    VLASTNI: 'Vlastní',
};

interface AkceFormData {
    nazev: string;
    popis: string;
    typ: string;
    misto: string;
    datumCasOd: Dayjs | null;
    datumCasDo: Dayjs | null;
    deadlineOdpovedi: Dayjs | null;
    opakovani: string;
    opakovaniIntervalDny: number | '';
    opakovaniKonec: Dayjs | null;
    viditelnost: string;
    uzivatelIds: number[];
    notifikacePriVytvoreni: boolean;
    notifikacePredAkciMinuty: number | '';
    typKontrolyId: number | null;
}

const emptyFormData: AkceFormData = {
    nazev: '',
    popis: '',
    typ: 'SKOLENI',
    misto: '',
    datumCasOd: null,
    datumCasDo: null,
    deadlineOdpovedi: null,
    opakovani: 'ZADNE',
    opakovaniIntervalDny: '',
    opakovaniKonec: null,
    viditelnost: 'VSICHNI',
    uzivatelIds: [],
    notifikacePriVytvoreni: true,
    notifikacePredAkciMinuty: 60,
    typKontrolyId: null,
};

const akceToFormData = (akce: Akce): AkceFormData => ({
    nazev: akce.nazev,
    popis: akce.popis || '',
    typ: akce.typ,
    misto: akce.misto || '',
    datumCasOd: akce.datumCasOd ? dayjs(akce.datumCasOd) : null,
    datumCasDo: akce.datumCasDo ? dayjs(akce.datumCasDo) : null,
    deadlineOdpovedi: akce.deadlineOdpovedi ? dayjs(akce.deadlineOdpovedi) : null,
    opakovani: akce.opakovani || 'ZADNE',
    opakovaniIntervalDny: akce.opakovaniIntervalDny || '',
    opakovaniKonec: akce.opakovaniKonec ? dayjs(akce.opakovaniKonec) : null,
    viditelnost: akce.viditelnost || 'VSICHNI',
    uzivatelIds: akce.vybraniUzivateleIds || [],
    notifikacePriVytvoreni: akce.notifikacePriVytvoreni ?? true,
    notifikacePredAkciMinuty: akce.notifikacePredAkciMinuty ?? 60,
    typKontrolyId: akce.typKontrolyId ?? null,
});

interface AkceFormDialogProps {
    open: boolean;
    /** null = vytvoreni nove akce, jinak editace existujici */
    akce: Akce | null;
    onClose: () => void;
    /** zavola se po uspesnem ulozeni (create i update) */
    onSaved?: () => void;
}

/**
 * Sdileny dialog pro vytvoreni i editaci akce. Pouziva se na seznamu akci
 * (AkcePage) i na detailu akce (DetailAkcePage). Po ulozeni invaliduje
 * query ["akce"], takze se prislusna stranka sama obnovi.
 */
const AkceFormDialog: React.FC<AkceFormDialogProps> = ({ open, akce, onClose, onSaved }) => {
    const [formData, setFormData] = useState<AkceFormData>(emptyFormData);

    const { data: allUsers } = useAllUsersQuery();
    const { data: typyKvalifikaci } = useTypyKontrolSplnitelneUcastiQuery();
    const createMutation = useCreateAkceMutation();
    const updateMutation = useUpdateAkceMutation();

    const activeUsers = useMemo(
        () => (allUsers || []).filter((u: Uzivatel) => u.aktivni),
        [allUsers],
    );

    // Synchronizace formulare pri otevreni / zmene editovane akce
    useEffect(() => {
        if (open) {
            setFormData(akce ? akceToFormData(akce) : emptyFormData);
        }
    }, [open, akce]);

    const buildRequest = (): CreateAkceRequest => ({
        nazev: formData.nazev.trim(),
        popis: formData.popis.trim() || undefined,
        typ: formData.typ,
        misto: formData.misto.trim() || undefined,
        datumCasOd: formData.datumCasOd?.format('YYYY-MM-DDTHH:mm:ss') || '',
        datumCasDo: formData.datumCasDo?.format('YYYY-MM-DDTHH:mm:ss') || undefined,
        deadlineOdpovedi: formData.deadlineOdpovedi?.format('YYYY-MM-DDTHH:mm:ss') || undefined,
        opakovani: formData.opakovani !== 'ZADNE' ? formData.opakovani : undefined,
        opakovaniIntervalDny: formData.opakovani === 'VLASTNI' && formData.opakovaniIntervalDny
            ? Number(formData.opakovaniIntervalDny) : undefined,
        opakovaniKonec: formData.opakovaniKonec?.format('YYYY-MM-DD') || undefined,
        viditelnost: formData.viditelnost,
        uzivatelIds: formData.uzivatelIds.length > 0 ? formData.uzivatelIds : undefined,
        notifikacePriVytvoreni: formData.notifikacePriVytvoreni,
        notifikacePredAkciMinuty: formData.notifikacePredAkciMinuty
            ? Number(formData.notifikacePredAkciMinuty) : undefined,
        typKontrolyId: formData.typKontrolyId ?? null,
    });

    const handleSubmit = () => {
        const request = buildRequest();
        if (akce) {
            updateMutation.mutate(
                { id: akce.id, data: request },
                { onSuccess: () => { onSaved?.(); onClose(); } },
            );
        } else {
            createMutation.mutate(request, {
                onSuccess: () => { onSaved?.(); onClose(); },
            });
        }
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;
    // Konec akce nesmi byt pred zacatkem (validuje se i na serveru)
    const datumDoPredOd = Boolean(
        formData.datumCasOd && formData.datumCasDo
        && formData.datumCasDo.isBefore(formData.datumCasOd),
    );
    const isFormValid = Boolean(formData.nazev.trim() && formData.typ && formData.datumCasOd)
        && !datumDoPredOd;

    return (
        <Dialog
            open={open}
            onClose={() => { if (!isSaving) onClose(); }}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>{akce ? 'Upravit akci' : 'Nová akce'}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Název akce"
                        value={formData.nazev}
                        onChange={(e) => setFormData({ ...formData, nazev: e.target.value })}
                        required
                        fullWidth
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Typ</InputLabel>
                            <Select
                                value={formData.typ}
                                label="Typ"
                                onChange={(e) => setFormData({ ...formData, typ: e.target.value })}
                            >
                                {Object.entries(TYP_LABELS).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Místo"
                            value={formData.misto}
                            onChange={(e) => setFormData({ ...formData, misto: e.target.value })}
                            fullWidth
                        />
                    </Box>

                    <FormControl fullWidth>
                        <InputLabel>Naplňuje typ kontroly (volitelné)</InputLabel>
                        <Select
                            value={formData.typKontrolyId != null ? String(formData.typKontrolyId) : ""}
                            label="Naplňuje typ kontroly (volitelné)"
                            onChange={(e) => setFormData({
                                ...formData,
                                typKontrolyId: e.target.value === "" ? null : Number(e.target.value),
                            })}
                        >
                            <MenuItem value=""><em>Žádná (jen docházka)</em></MenuItem>
                            {typyKvalifikaci?.map((t) => (
                                <MenuItem key={t.id} value={String(t.id)}>{t.nazev}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <MobileDateTimePicker
                            label="Datum a čas od *"
                            value={formData.datumCasOd}
                            onChange={(val) => setFormData({ ...formData, datumCasOd: val })}
                            slotProps={{ textField: { fullWidth: true } }}
                            ampm={false}
                            format="D.M.YYYY HH:mm"
                        />
                        <MobileDateTimePicker
                            label="Datum a čas do"
                            value={formData.datumCasDo}
                            onChange={(val) => setFormData({ ...formData, datumCasDo: val })}
                            minDateTime={formData.datumCasOd ?? undefined}
                            slotProps={{ textField: {
                                fullWidth: true,
                                error: datumDoPredOd,
                                helperText: datumDoPredOd
                                    ? 'Konec akce nesmí být před začátkem'
                                    : undefined,
                            } }}
                            ampm={false}
                            format="D.M.YYYY HH:mm"
                        />
                    </Box>

                    <TextField
                        label="Popis"
                        value={formData.popis}
                        onChange={(e) => setFormData({ ...formData, popis: e.target.value })}
                        multiline
                        rows={3}
                        fullWidth
                    />

                    <MobileDateTimePicker
                        label="Deadline pro odpovědi"
                        value={formData.deadlineOdpovedi}
                        onChange={(val) => setFormData({ ...formData, deadlineOdpovedi: val })}
                        slotProps={{ textField: { fullWidth: true } }}
                        ampm={false}
                        format="D.M.YYYY HH:mm"
                    />

                    {/* Viditelnost + Výběr uživatelů */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl sx={{ width: 160 }}>
                            <InputLabel>Viditelnost</InputLabel>
                            <Select
                                value={formData.viditelnost}
                                label="Viditelnost"
                                onChange={(e) => setFormData({ ...formData, viditelnost: e.target.value })}
                            >
                                <MenuItem value="VSICHNI">Všichni</MenuItem>
                                <MenuItem value="VYBRANI">Vybraní</MenuItem>
                            </Select>
                            <FormHelperText>
                                {formData.viditelnost === 'VSICHNI'
                                    ? 'Akci vidí všichni, notifikace jen vybraným (nebo všem pokud nevyberete nikoho)'
                                    : 'Akci vidí, odpovídají a notifikaci dostanou pouze vybraní členové'}
                            </FormHelperText>
                        </FormControl>
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={activeUsers}
                            getOptionLabel={(option: Uzivatel) => option.jmeno}
                            isOptionEqualToValue={(option: Uzivatel, val: Uzivatel) => option.id === val.id}
                            value={activeUsers.filter((u: Uzivatel) => formData.uzivatelIds.includes(u.id))}
                            onChange={(_event, newValue) => {
                                setFormData({
                                    ...formData,
                                    uzivatelIds: newValue.map((u: Uzivatel) => u.id),
                                });
                            }}
                            renderOption={(props, option: Uzivatel, { selected }) => {
                                const { key, ...optionProps } = props as typeof props & { key: string };
                                return (
                                    <li key={key} {...optionProps}>
                                        <Checkbox
                                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option.jmeno}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Vybraní členové"
                                    helperText={
                                        formData.viditelnost === 'VSICHNI'
                                            ? 'Prázdné = notifikace všem'
                                            : 'Povinné pro viditelnost "Vybraní"'
                                    }
                                />
                            )}
                            sx={{ flex: 1 }}
                        />
                    </Box>

                    {/* Opakování */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl sx={{ minWidth: 160 }}>
                            <InputLabel>Opakování</InputLabel>
                            <Select
                                value={formData.opakovani}
                                label="Opakování"
                                onChange={(e) => setFormData({ ...formData, opakovani: e.target.value })}
                            >
                                {Object.entries(OPAKOVANI_LABELS).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {formData.opakovani === 'VLASTNI' && (
                            <TextField
                                label="Interval (dny)"
                                type="number"
                                value={formData.opakovaniIntervalDny}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    opakovaniIntervalDny: e.target.value ? Number(e.target.value) : '',
                                })}
                                sx={{ width: 140 }}
                            />
                        )}
                        {formData.opakovani !== 'ZADNE' && (
                            <MobileDatePicker
                                label="Opakování do"
                                value={formData.opakovaniKonec}
                                onChange={(val) => setFormData({ ...formData, opakovaniKonec: val })}
                                slotProps={{ textField: { fullWidth: true } }}
                                format="D.M.YYYY"
                            />
                        )}
                    </Box>

                    {/* Notifikace */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.notifikacePriVytvoreni}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        notifikacePriVytvoreni: e.target.checked,
                                    })}
                                />
                            }
                            label="Notifikace při vytvoření"
                        />
                        <TextField
                            label="Připomínka (minuty před)"
                            type="number"
                            value={formData.notifikacePredAkciMinuty}
                            onChange={(e) => setFormData({
                                ...formData,
                                notifikacePredAkciMinuty: e.target.value ? Number(e.target.value) : '',
                            })}
                            sx={{ width: 200 }}
                            helperText="0 = bez připomínky"
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Zrušit</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!isFormValid || isSaving}
                >
                    {isSaving && <CircularProgress size={20} sx={{ mr: 1 }} />}
                    {akce ? 'Uložit' : 'Vytvořit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AkceFormDialog;
